import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import { EmptyState, ScreenHeader, IconButton } from '../components/UI';
import Icon from '../components/Icon';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
}

export default function FAQ() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get<FaqItem[]>(`${API_BASE}/faqs`);
      setFaqs(res.data);
      setError('');
    } catch {
      setError('Failed to load FAQs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <ScreenHeader
        title="FAQ"
        icon="question-circle"
        right={<IconButton icon="arrow-left" accessibilityLabel="Back" onPress={() => navigate(-1)} />}
      />
      <div style={{ padding: 20, paddingBottom: 40 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{
              width: 28, height: 28, border: `3px solid ${colors.accentSoft}`,
              borderTopColor: colors.accentDark, borderRadius: '50%',
              animation: 'aga-spin 0.7s linear infinite',
            }} />
          </div>
        ) : error ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            backgroundColor: colors.dangerSoft, border: `1px solid ${colors.danger}`,
            borderRadius: 10, padding: 14,
          }}>
            <Icon name="exclamation-circle" size={16} color={colors.dangerText} />
            <div style={{ fontSize: 14, color: colors.dangerText, flex: 1 }}>{error}</div>
          </div>
        ) : faqs.length === 0 ? (
          <EmptyState icon="question-circle" title="No FAQs yet" body="Check back later for answers to common questions." />
        ) : (
          faqs.map(faq => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                style={{
                  borderRadius: 12, border: `1px solid ${colors.cardBorder}`,
                  backgroundColor: colors.card, marginBottom: 10, overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
              >
                <button
                  onClick={() => setOpenId(prev => prev === faq.id ? null : faq.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', padding: 16, minHeight: 58,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ flex: 1, fontSize: 15, fontWeight: 700, lineHeight: 1.45, color: colors.text, marginRight: 12 }}>
                    {faq.question}
                  </div>
                  <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={13} color={colors.textMuted} />
                </button>
                {isOpen && (
                  <div style={{ padding: '0 16px 16px' }}>
                    <div style={{ height: 1, backgroundColor: colors.divider, marginBottom: 12 }} />
                    <div style={{ fontSize: 14, lineHeight: 1.55, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
